import React, { useState, useMemo, useEffect } from "react";
import T from '../../components/common/T';
import { useAssets } from "../../context/AssetContext";
import { useNavigate, useLocation } from "react-router-dom";
import AssetListView from "./AssetListView";
import AssetKanbanView from "./AssetKanbanView"; 
import { useCurrency } from "../../hooks/useCurrency";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import useModuleFilters from "../../hooks/useModuleFilters";
import { 
    WrenchScrewdriverIcon, TruckIcon, CheckCircleIcon, 
    ExclamationTriangleIcon, XMarkIcon, ChartBarIcon
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

const AssetPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { assets = [], deleteAsset, loading, fetchAssets, getAssetById } = useAssets();
  const { formatMoney } = useCurrency();
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState(() => localStorage.getItem('assetView') || 'list');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("assets", {
    categories: [],
    statuses: [],
    projects: [],
  });

  useEffect(() => {
    const handleDeepLink = async () => {
        const queryParams = new URLSearchParams(location.search);
        const assetId = queryParams.get('id');

        if (assetId) {
            const existing = assets.find(a => String(a.id) === String(assetId));
            if (existing) {
                setSelectedAsset(existing);
            } else {
                const fetched = await getAssetById(assetId);
                if (fetched) {
                    setSelectedAsset(fetched);
                } else {
                    toast.error("Asset not found or restricted.");
                }
            }
            navigate(location.pathname, { replace: true });
        }
    };
    handleDeepLink();
  }, [location.search, assets, getAssetById, navigate, location.pathname]);

  const handleViewChange = (newView) => {
    setView(newView);
    localStorage.setItem('assetView', newView);
  };

  const categories = useMemo(() => {
    const set = new Set();
    assets.forEach(a => { if (a.category) set.add(a.category); });
    return Array.from(set);
  }, [assets]);

  const filtered = useMemo(() => {
    return assets.filter(asset => {
      const q = searchTerm.toLowerCase();
      const matchesSearch = !q ||
        asset.name?.toLowerCase().includes(q) || 
        asset.code?.toLowerCase().includes(q) ||
        asset.location?.toLowerCase().includes(q);
      const matchesStatus = filterStatus === "all" || asset.status?.toLowerCase() === filterStatus.toLowerCase();
      const matchesCategory = filterCategory === "all" || asset.category === filterCategory;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [assets, searchTerm, filterStatus, filterCategory]);

  const statsCounts = useMemo(() => ({
    total: assets.length,
    available: assets.filter(a => a.status === 'Available').length,
    inUse: assets.filter(a => a.status === 'In Use').length,
    maintenance: assets.filter(a => a.status === 'Under Maintenance').length,
    retired: assets.filter(a => a.status === 'Retired').length,
  }), [assets]);

  const stats = useMemo(() => [
    {
      label: "Total Assets",
      value: statsCounts.total,
      icon: ChartBarIcon,
      variant: "default",
      isActive: filterStatus === "all" || !filterStatus,
      onClick: () => { setFilterStatus("all"); setFilterCategory("all"); },
    },
    {
      label: "Available",
      value: statsCounts.available,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filterStatus === "Available",
      onClick: () => setFilterStatus(s => s === "Available" ? "all" : "Available"),
    },
    {
      label: "Deployed",
      value: statsCounts.inUse,
      icon: TruckIcon,
      variant: "blue",
      isActive: filterStatus === "In Use",
      onClick: () => setFilterStatus(s => s === "In Use" ? "all" : "In Use"),
    },
    {
      label: "Maintenance",
      value: statsCounts.maintenance,
      icon: ExclamationTriangleIcon,
      variant: "orange",
      isActive: filterStatus === "Under Maintenance",
      onClick: () => setFilterStatus(s => s === "Under Maintenance" ? "all" : "Under Maintenance"),
    },
    {
      label: "Retired",
      value: statsCounts.retired,
      icon: WrenchScrewdriverIcon,
      variant: "gray",
      isActive: filterStatus === "Retired",
      onClick: () => setFilterStatus(s => s === "Retired" ? "all" : "Retired"),
    },
  ], [statsCounts, filterStatus]);

  const getStatusBadge = (status) => {
    const badges = {
      'Available': <span className="bg-green-100 dark:bg-emerald-950/60 text-green-700 dark:text-emerald-400 border border-green-200 dark:border-emerald-800/60 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 w-fit"><CheckCircleIcon className="w-3 h-3"/> Available</span>,
      'In Use': <span className="bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/60 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 w-fit"><TruckIcon className="w-3 h-3"/> Deployed</span>,
      'Under Maintenance': <span className="bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800/60 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 w-fit"><ExclamationTriangleIcon className="w-3 h-3"/> Maintenance</span>,
      'Retired': <span className="bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700 px-2 py-0.5 rounded-full text-xs font-bold">Retired</span>,
    };
    return badges[status] || <span className="bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700 px-2 py-0.5 rounded-full text-xs font-bold">{status}</span>;
  };

  const handleRefresh = async () => { 
    if (fetchAssets) await fetchAssets(); 
    toast.success("Assets refreshed!"); 
  };
  const clearFilters = () => { setSearchTerm(""); setFilterStatus("all"); setFilterCategory("all"); };

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        icon={WrenchScrewdriverIcon}
        iconColor="text-[#E8612C]"
        title="Asset Management"
        subtitle="Track equipment lifecycle, status & deployment"
        onRefresh={handleRefresh}
        loading={loading}
      >
        <TopPrimaryButton
          onClick={() => navigate("/admin/assets/add")}
          label="New Asset"
        />
      </PageHeader>

      {/* Stats */}
      <StatsGrid stats={stats} cols={5} />

      {/* Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search assets, codes, locations..."
        tabs={['All', 'Available', 'In Use', 'Under Maintenance', 'Retired']}
        activeTab={filterStatus === 'all' ? 'All' : filterStatus}
        onTabChange={(tab) => setFilterStatus(tab === 'All' ? 'all' : tab)}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      >
        <ViewSwitcher view={view} onViewChange={handleViewChange} availableViews={['list', 'kanban']} />
      </Toolbar>

      {/* Filter Panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={clearFilters}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Category</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            disabled={loadingFilters}
            className="w-full h-8 px-2.5 text-xs bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="all"><T>All Categories</T></option>
            {((filterOptions.categories?.length > 0 ? filterOptions.categories : categories) || []).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {/* Content */}
      <TableCard>
        {loading ? (
          <div className="p-10 text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E8612C] mx-auto"></div></div>
        ) : view === "list" ? (
          <AssetListView assets={filtered} onDetails={setSelectedAsset} onEdit={(id) => navigate(`/admin/assets/edit/${id}`)} onDelete={deleteAsset} getStatusBadge={getStatusBadge} />
        ) : (
          <AssetKanbanView assets={filtered} onDetails={setSelectedAsset} onEdit={(id) => navigate(`/admin/assets/edit/${id}`)} />
        )}
      </TableCard>

      {/* Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-xs p-3 sm:p-4">
          <div className="bg-white dark:bg-[#161b22] rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-2xl animate-fade-in overflow-hidden border border-gray-200 dark:border-[#30363d]">
            <div className="bg-gradient-to-r from-[#E8612C] to-[#cf4f1d] px-4 sm:px-6 py-3 text-white flex justify-between items-center">
              <h3 className="text-sm font-bold flex items-center gap-2"><WrenchScrewdriverIcon className="w-5 h-5" /> Asset Details</h3>
              <button onClick={() => setSelectedAsset(null)} className="hover:bg-white/20 p-1 rounded-lg transition cursor-pointer"><XMarkIcon className="w-5 h-5"/></button>
            </div>
            <div className="p-4 sm:p-6 space-y-3 max-h-[65vh] overflow-y-auto">
              <div className="bg-gray-50 dark:bg-[#21262d] p-3 rounded-lg border border-gray-200 dark:border-[#30363d]"><p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold mb-0.5"><T>Asset Name</T></p><p className="font-bold text-lg sm:text-2xl text-gray-800 dark:text-white">{selectedAsset.name}</p></div>
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div className="bg-white dark:bg-[#21262d] p-3 rounded-lg border border-gray-200 dark:border-[#30363d]"><p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold mb-0.5"><T>Code</T></p><p className="font-mono font-bold text-sm sm:text-lg text-gray-700 dark:text-slate-200">{selectedAsset.code}</p></div>
                <div className="bg-white dark:bg-[#21262d] p-3 rounded-lg border border-gray-200 dark:border-[#30363d]"><p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold mb-0.5"><T>Category</T></p><p className="font-medium text-sm text-gray-700 dark:text-slate-200">{selectedAsset.category}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div className="bg-white dark:bg-[#21262d] p-3 rounded-lg border border-gray-200 dark:border-[#30363d]"><p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold mb-1"><T>Status</T></p><div className="mt-1">{getStatusBadge(selectedAsset.status)}</div></div>
                <div className="bg-white dark:bg-[#21262d] p-3 rounded-lg border border-gray-200 dark:border-[#30363d]"><p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold mb-0.5"><T>Cost Rate</T></p><p className="font-bold text-sm sm:text-lg text-[#E8612C]">{formatMoney(selectedAsset.cost_rate)}/hr</p></div>
              </div>
              {selectedAsset.project_name && <div className="bg-blue-50 dark:bg-blue-950/40 p-3 rounded-lg border border-blue-200 dark:border-blue-800/60"><p className="text-[10px] text-blue-700 dark:text-blue-400 uppercase font-bold mb-0.5"><T>Assigned Project</T></p><p className="font-bold text-sm text-blue-900 dark:text-blue-200">{selectedAsset.project_name}</p></div>}
              {selectedAsset.notes && <div className="bg-white dark:bg-[#21262d] p-3 rounded-lg border border-gray-200 dark:border-[#30363d]"><p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold mb-0.5"><T>Notes</T></p><p className="text-xs sm:text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">{selectedAsset.notes}</p></div>}
            </div>
            <div className="bg-gray-50 dark:bg-[#21262d] px-4 sm:px-6 py-3 flex justify-end gap-2 border-t border-gray-200 dark:border-[#30363d]">
              <button onClick={() => setSelectedAsset(null)} className="px-3 sm:px-5 py-1.5 sm:py-2 bg-gray-200 dark:bg-[#30363d] text-gray-700 dark:text-slate-200 rounded-lg text-xs font-medium hover:bg-gray-300 dark:hover:bg-slate-700 transition cursor-pointer">Close</button>
              <button onClick={() => { setSelectedAsset(null); navigate(`/admin/assets/edit/${selectedAsset.id}`); }} className="px-3 sm:px-5 py-1.5 sm:py-2 bg-[#E8612C] text-white rounded-lg text-xs font-bold hover:bg-[#cf4f1d] transition shadow-xs cursor-pointer">Edit Asset</button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default AssetPage;